import { UserType } from "@/constants";
import { getRegistrantAccountOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  JsonValueType,
  LicenseQuestionAnswerType,
  LicenseResponseSchema,
  LicenseTypeResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import {
  answerOptionsToSelectOptions,
  getFirstAnswerOptionKey,
} from "@/lib/answer-option-order";
import {
  deriveAnswerRequiredForContactQuestion,
  nacFieldRequirementsForValidation,
} from "@/lib/contact-question-requirements";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { isPaymentScreenAttestation } from "@/lib/payment-screen-attestation";
import { getVisibleQuestionsWithJumpLogic } from "@/lib/question-jump-logic";
import { cn } from "@/lib/utils";
import type { LicenseAnswers } from "@/types/license";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import React, { useEffect } from "react";
import { Alert, AlertDescription } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { FormFieldWrapper } from "../../ui/form-field-wrapper";
import { RichTextHtml } from "../../ui/rich-text-html";
import { useAppForm } from "../../ui/tanstack-form";
import type { PreviousNameAddressContactOption } from "../form-utils/question-field-utils";
import {
  NAC_FILLED_BY_CHECKBOX_KEY,
  QuestionField,
  companyCopyFieldProps,
  extractBracketLabel,
  isFilledByCheckbox,
  validatorForQuestionType,
} from "../form-utils/question-field-utils";

interface LicenseTypeQuestionsFormProps {
  onPrevious?: () => Promise<void>;
  onNext?: (answers: LicenseAnswers) => Promise<void>;
  licenseType: LicenseTypeResponseSchema;
  initialAnswers?: Partial<LicenseAnswers>;
  stepNumber: number;
  isRenewal: boolean;
  licenseData?: LicenseResponseSchema;
  questionsData: {
    questions: QuestionResponseSchema[];
  };
  userType?: UserType;
  accountData?: any;
  /** When `"none"`, hides Previous/Next (e.g. operator preview dialog with external close only). */
  footerActions?: "default" | "none";
  /** Treat every question as optional (unregistered-property create/edit). */
  allQuestionsOptional?: boolean;
  /** Include the synthetic effective-date question. Defaults to true. */
  includeEffectiveDateQuestion?: boolean;
  /**
   * Keep the synthetic effective-date answer fixed. Used when the registrant
   * is transferring an existing registration, whose start date must not change.
   */
  lockEffectiveDate?: boolean;
  /** Called whenever form values change; used to persist live answers without Next. */
  onAnswersChange?: (answers: LicenseAnswers) => void;
  /** Existing registrant whose other properties supply previous property managers. */
  registrantAccountId?: string | null;
  submitLabel?: string;
  cardTitle?: string;
}

type FormAnswerType = JsonValueType | Date;

// Add this new component for Information questions
function InformationField({
  content,
  question,
}: { content: string; question: string }) {
  return (
    <div className="space-y-2">
      <h3 className="font-medium text-sm">{question}</h3>
      <div className="rounded-md bg-muted/50 border">
        <RichTextHtml html={content} className="p-4" />
      </div>
    </div>
  );
}

function AnswersChangeNotifier({
  values,
  onAnswersChange,
}: {
  values: LicenseAnswers;
  onAnswersChange: (answers: LicenseAnswers) => void;
}) {
  useEffect(() => {
    onAnswersChange(values);
  }, [onAnswersChange, values]);
  return null;
}

export function LicenseTypeQuestionsForm({
  onPrevious,
  onNext,
  licenseType,
  initialAnswers,
  stepNumber,
  isRenewal,
  licenseData,
  questionsData,
  userType = UserType.REGISTRANT,
  accountData: accountDataProp,
  footerActions = "default",
  allQuestionsOptional = false,
  includeEffectiveDateQuestion = true,
  lockEffectiveDate = false,
  onAnswersChange,
  submitLabel,
  cardTitle,
  registrantAccountId,
}: LicenseTypeQuestionsFormProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  // Get account data for "I am the... " functionality
  const { data: accountDataFromQuery } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete && !accountDataProp, // Only query if no prop data provided (i.e., not in operator flow)
  });

  // Use prop accountData if provided (for operator flow), otherwise use queried data (for registrant flow)
  const accountData = accountDataProp || accountDataFromQuery;

  const allQuestions = React.useMemo(() => {
    if (!questionsData?.questions) return [];

    const effectiveDateQuestion: QuestionResponseSchema = {
      id: "effective_date",
      question: licenseType.effective_date_label,
      answer_type: "date",
      answer_options: null,
      display_order: 1,
      answer_required: true,
      question_type: "registration",
    };

    // Only show registration questions (exclude inspection and deregistration)
    const registrationQuestions = questionsData.questions.filter(
      (q) => q.question_type === "registration",
    );

    // Up-front information is on the address step. Payment-screen confirmations
    // (such as a perjury attestation) are collected on the fees step.
    const filteredQuestions = registrationQuestions.filter(
      (q) =>
        !(q.answer_type === "information" && q.display_up_front === true) &&
        !isPaymentScreenAttestation(q),
    );

    // Use full list (sorted by display_order); visibility is computed at render from current answers via getVisibleQuestionsWithJumpLogic so conditional jump-to (e.g. 200) can show questions that would be skipped by unconditional flow
    const sorted = [...filteredQuestions].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
    );
    return includeEffectiveDateQuestion
      ? [effectiveDateQuestion, ...sorted]
      : sorted;
  }, [
    includeEffectiveDateQuestion,
    licenseType.effective_date_label,
    questionsData?.questions,
  ]);

  const defaultValues = React.useMemo(() => {
    return allQuestions.reduce(
      (acc, question) => {
        // Skip Information type questions as they don't need form state
        if (question.answer_type === "information") {
          return acc;
        }

        const answer = initialAnswers?.[question.id];
        if (answer !== undefined && answer !== null && answer !== "") {
          acc[question.id] = answer;
        } else if (question.answer_type === "multi_select") {
          // Initialise multi-select as an empty array
          acc[question.id] = [];
        } else if (
          question.answer_type === "single_select" &&
          question.answer_options &&
          Object.keys(question.answer_options).length > 0
        ) {
          // First option in configured (API) order when no prior answer
          const first = getFirstAnswerOptionKey(
            question.answer_options,
            question.answer_option_keys_order,
          );
          if (first !== undefined) {
            acc[question.id] = first;
          }
        }
        // Boolean: leave unset so Yes/No radios start with nothing selected
        return acc;
      },
      {} as Record<string, FormAnswerType>,
    );
  }, [allQuestions, initialAnswers]);

  const lockedEffectiveDate = lockEffectiveDate
    ? (initialAnswers?.effective_date ?? null)
    : null;
  const lockedEffectiveDateRef = React.useRef(lockedEffectiveDate);
  lockedEffectiveDateRef.current = lockedEffectiveDate;

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      // Strip internal _filledByCheckbox from name_address_contact answers before sending to API
      const sanitized = { ...value } as Record<string, unknown>;
      const nacIds = new Set(
        allQuestions
          .filter((q) => isNacStyleAnswerType(q.answer_type))
          .map((q) => q.id),
      );
      for (const id of nacIds) {
        const v = sanitized[id];
        if (
          v &&
          typeof v === "object" &&
          NAC_FILLED_BY_CHECKBOX_KEY in (v as object)
        ) {
          const { [NAC_FILLED_BY_CHECKBOX_KEY]: _, ...rest } = v as Record<
            string,
            unknown
          >;
          sanitized[id] = rest;
        }
      }
      if (lockedEffectiveDateRef.current) {
        sanitized.effective_date = lockedEffectiveDateRef.current;
      }
      await onNext?.(sanitized as LicenseAnswers);
    },
  });

  // useAppForm only applies defaultValues on first mount. When questions arrive from
  // the API (or initialAnswers change), reset so single_select fields get the first
  // configured option. Skip when the baseline is unchanged so background refetches
  // with the same shape do not wipe user input.
  const formBaselineSig = React.useMemo(
    () =>
      JSON.stringify({
        ids: allQuestions.map((q) => q.id),
        opts: allQuestions.map((q) =>
          q.answer_options ? Object.keys(q.answer_options) : null,
        ),
        // Same keys can appear in different display/default order via operator reorder.
        optionOrder: allQuestions.map(
          (q) => q.answer_option_keys_order ?? null,
        ),
        initial: initialAnswers ?? null,
      }),
    [allQuestions, initialAnswers],
  );

  const formBaselineAppliedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (formBaselineAppliedRef.current === formBaselineSig) {
      return;
    }
    formBaselineAppliedRef.current = formBaselineSig;
    form.reset(defaultValues);
  }, [formBaselineSig, defaultValues, form.reset]);

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = await form.validateAllFields("submit");
    if (errors.length === 0) {
      await form.handleSubmit();
    }
  };

  const handlePrevious = (e: React.FormEvent) => {
    if (onPrevious) {
      e.preventDefault();
      e.stopPropagation();
      onPrevious();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {cardTitle ??
            (isRenewal
              ? `Step ${stepNumber}: Confirm Property Details`
              : `Step ${stepNumber}: Additional Information`)}
        </CardTitle>
        {isRenewal && (
          <CardDescription>
            Update or confirm the property details for renewal
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {onAnswersChange ? (
          <form.Subscribe selector={(state) => state.values}>
            {(values) => (
              <AnswersChangeNotifier
                values={values as LicenseAnswers}
                onAnswersChange={onAnswersChange}
              />
            )}
          </form.Subscribe>
        ) : null}
        <div className="space-y-6">
          {isRenewal &&
            licenseData?.property &&
            licenseData?.status !== "REJECTED" && (
              <Alert variant="info">
                <AlertDescription>
                  <p>
                    You are renewing the registration for{" "}
                    <strong>{licenseData.property.display_address}</strong>.
                    Please review and update the information below as needed.
                  </p>
                </AlertDescription>
              </Alert>
            )}

          <form className="space-y-6" onSubmit={handleNext}>
            <form.Subscribe selector={(state) => state.values}>
              {(currentValues) => {
                // Path-based visibility: unconditional + conditional jump so e.g. jump-to 200 shows question at display_order 200
                const visibleQuestions = getVisibleQuestionsWithJumpLogic(
                  allQuestions,
                  currentValues as Record<string, unknown>,
                );
                return (
                  <>
                    {visibleQuestions.map((question, index) => (
                      <React.Fragment key={question.id}>
                        {question.answer_type === "information" &&
                        question.information_text ? (
                          <InformationField
                            content={question.information_text}
                            question={question.question}
                          />
                        ) : (
                          <form.AppField
                            name={question.id}
                            validators={
                              question.answer_type !== "information"
                                ? {
                                    onChange: validatorForQuestionType(
                                      question.answer_type as Exclude<
                                        LicenseQuestionAnswerType,
                                        "information"
                                      >,
                                      allQuestionsOptional
                                        ? false
                                        : question.answer_type === "boolean" ||
                                            question.answer_type ===
                                              "single_select" ||
                                            (question.answer_required ?? true),
                                      isNacStyleAnswerType(question.answer_type)
                                        ? (question.select_from_fixed_list ??
                                            false)
                                        : undefined,
                                      isNacStyleAnswerType(question.answer_type)
                                        ? nacFieldRequirementsForValidation(
                                            question,
                                          )
                                        : undefined,
                                      question.allow_multiple_urls ?? false,
                                      question.allow_multiple_documents ??
                                        false,
                                    ),
                                  }
                                : undefined
                            }
                          >
                            {(field) => (
                              <FormFieldWrapper
                                field={field}
                                label={
                                  <>
                                    {question.question}
                                    {(isNacStyleAnswerType(question.answer_type)
                                      ? !deriveAnswerRequiredForContactQuestion(
                                          question.answer_type,
                                          nacFieldRequirementsForValidation(
                                            question,
                                          ),
                                        )
                                      : !question.answer_required) && (
                                      <span className="text-xs text-muted-foreground ml-2" />
                                    )}
                                  </>
                                }
                                formMessage={
                                  question.id === "effective_date" &&
                                  lockEffectiveDate ? (
                                    <span className="text-xs text-muted-foreground">
                                      This date comes from the existing
                                      registration and cannot be changed.
                                    </span>
                                  ) : index === 0 && isRenewal ? (
                                    <span className="text-xs text-muted-foreground">
                                      Please confirm the start date. This helps
                                      us determine if the property is still
                                      eligible for renewal.
                                    </span>
                                  ) : undefined
                                }
                              >
                                <QuestionField
                                  disabled={
                                    lockEffectiveDate &&
                                    question.id === "effective_date"
                                  }
                                  answerType={question.answer_type}
                                  allowMultipleUrls={
                                    question.allow_multiple_urls ?? false
                                  }
                                  allowMultipleDocuments={
                                    question.allow_multiple_documents ?? false
                                  }
                                  field={field}
                                  questionId={question.id}
                                  confirmationText={question.information_text}
                                  selectFromFixedList={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? (question.select_from_fixed_list ??
                                        false)
                                      : undefined
                                  }
                                  allowCopyRegistrantInfo={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? (question.allow_copy_registrant_info ??
                                        true)
                                      : undefined
                                  }
                                  nacRequirements={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? nacFieldRequirementsForValidation(
                                          question,
                                        )
                                      : undefined
                                  }
                                  useContactLabel={
                                    question.answer_type ===
                                      "company_contact" &&
                                    Boolean(question.use_contact_label)
                                  }
                                  answerOptions={
                                    question.answer_type === "single_select" ||
                                    question.answer_type === "multi_select"
                                      ? question.answer_options
                                        ? answerOptionsToSelectOptions(
                                            question.answer_options,
                                            question.answer_option_keys_order,
                                          )
                                        : undefined
                                      : undefined
                                  }
                                  questionName={question.question}
                                  accountData={accountData}
                                  userType={userType}
                                  previousNameAddressContactOptions={(() => {
                                    if (
                                      !isNacStyleAnswerType(
                                        question.answer_type,
                                      )
                                    )
                                      return undefined;
                                    const prevNac = visibleQuestions
                                      .slice(0, index)
                                      .filter((q) =>
                                        isNacStyleAnswerType(q.answer_type),
                                      );
                                    const options: PreviousNameAddressContactOption[] =
                                      [];
                                    for (const q of prevNac) {
                                      const bracket = extractBracketLabel(
                                        q.question,
                                      );
                                      const ans = (
                                        currentValues as Record<string, unknown>
                                      )[q.id];
                                      // Exclude questions filled by "I am the X" or "Y is the X" from copy-from options
                                      if (bracket && !isFilledByCheckbox(ans)) {
                                        options.push({
                                          questionId: q.id,
                                          bracketLabel: bracket,
                                          answer: ans ?? {},
                                        });
                                      }
                                    }
                                    return options.length > 0
                                      ? options
                                      : undefined;
                                  })()}
                                  previousNacLiveValues={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? (() => {
                                          const prevNac = visibleQuestions
                                            .slice(0, index)
                                            .filter((q) =>
                                              isNacStyleAnswerType(
                                                q.answer_type,
                                              ),
                                            );
                                          const options: PreviousNameAddressContactOption[] =
                                            [];
                                          for (const q of prevNac) {
                                            const bracket = extractBracketLabel(
                                              q.question,
                                            );
                                            const ans = (
                                              currentValues as Record<
                                                string,
                                                unknown
                                              >
                                            )[q.id];
                                            if (
                                              bracket &&
                                              !isFilledByCheckbox(ans)
                                            ) {
                                              options.push({
                                                questionId: q.id,
                                                bracketLabel: bracket,
                                                answer: ans ?? {},
                                              });
                                            }
                                          }
                                          const live: Record<string, unknown> =
                                            {};
                                          for (const o of options) {
                                            live[o.questionId] = (
                                              currentValues as Record<
                                                string,
                                                unknown
                                              >
                                            )[o.questionId];
                                          }
                                          return options.length > 0
                                            ? live
                                            : undefined;
                                        })()
                                      : undefined
                                  }
                                  currentQuestionBracketLabel={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? (extractBracketLabel(
                                          question.question,
                                        ) ?? undefined)
                                      : undefined
                                  }
                                  {...companyCopyFieldProps({
                                    question,
                                    questions: visibleQuestions,
                                    answers: currentValues as Record<
                                      string,
                                      unknown
                                    >,
                                    registrantAccountId:
                                      registrantAccountId ??
                                      licenseData?.registrant_account?.id,
                                    excludeLicenseId: licenseData?.id,
                                  })}
                                />
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                        )}
                      </React.Fragment>
                    ))}
                  </>
                );
              }}
            </form.Subscribe>
          </form>
        </div>
      </CardContent>
      {footerActions === "default" ? (
        <CardFooter
          className={cn(
            "flex justify-between",
            !onPrevious ? "flex-row-reverse" : "",
          )}
        >
          {onPrevious && (
            <Button variant="outline" onClick={handlePrevious}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
          )}
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                className="disabled:opacity-20 disabled:cursor-not-allowed"
                onClick={handleNext}
                disabled={!canSubmit}
              >
                {isSubmitting ? "Saving..." : (submitLabel ?? "Next")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </form.Subscribe>
        </CardFooter>
      ) : null}
    </Card>
  );
}
